import { Fragment, useId, useState, type ReactNode } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, ChevronDown, ChevronUp, ExternalLink, Filter, Handshake, Info, Mail, MoreHorizontal, Phone, Plus, Star, Upload, Users, X } from 'lucide-react';
import { intercalarHistorico, OrigemDoHistorico, useHistoricoDoParceiro, type RegistroDoHistorico } from './historico-do-parceiro';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Cartao, Indicador, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Entrada, linhasExtras, Rotulo, Selecao, useCamposPersonalizados, valorExtra } from '../../componentes/formulario';
import { Menu, Popover, type ItemMenu } from '../../componentes/menu';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { ArquivarParceiro } from './ArquivarParceiro';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, email, endereco, nomePessoa, numero, telefoneFormatado, textoLivre } from '../../nucleo/formato';
import { rotuloEtapaPipeline, rotuloFunilPipeline } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { useSubsistema } from '../../nucleo/subsistemas';
import { paginaNoSubsistema } from '@malgoos/shared';
import { AtalhoDeTarefas } from '../../nucleo/atalho-tarefas';
import { ICONE_FEEDBACK, useFeedback } from '../../componentes/feedback';
import { DialogoCriacao, useAbrirCriacao, useContextoDaCriacao, useCriacaoDaChave } from '../../nucleo/criacao-rapida';
import { ImportarCsv } from './ImportarCsv';
import { CartaoVerticaisRelacionamento, chaveVerticaisRelacionamento } from './VerticaisRelacionamento';
import type { Ficha as FichaParceiro } from './tipos';
import {
  AdicionarContatoEmpresa,
  BarraSelecao,
  baixarCsv,
  CabecalhoPagina,
  CAMINHO_CONTATOS,
  CAMINHO_EMPRESAS,
  caminhoDaNegociacaoCrm,
  ConfirmarContagem,
  CriarNegociacao,
  CriarNegociacaoEmLote,
  emMassa,
  FiltroPeriodo,
  FiltrosSalvos,
  GavetaSobre,
  idsDoFiltro,
  Iniciais,
  corpoDosResponsaveis,
  textoDosResponsaveis,
  EnderecoEditavel,
  EXTRAS_FORA_DO_CRM,
  ExtraEditavel,
  LinhaEditavel,
  LinhaMultiplaEditavel,
  registrosPorIds,
  RESPONSAVEL_TODOS,
  responsaveisDoRegistro,
  type DadosEnderecoRapido,
  useAvisoDeReleitura,
  useAvisoEmMassa,
  useMembros,
  usePodeNegociar,
  useSetores,
  type ParceiroCrm, registroInacessivel } from './crm-comum';
import { contarFiltros, paramsDosFiltros } from './crm-filtros';
import { statusDaNegociacao } from './CrmContatos';
import { CartaoFiliais, CartaoMatriz } from './CrmFiliais';
import { FormContato, FormEmpresa } from './CrmFormularios';
import { useSelecaoCrm } from './crm-selecao';
import { LinkVoltar } from '../../nucleo/voltar';

/**
 * CRM no novo desenho (lote 1, C-C): página Empresas do clone do RD sobre o cadastro único (`papel=empresa`). Lista com
 * "Todas / Minhas empresas", filtros predefinidos de vendas, filtros completos, seleção em massa com a contagem como
 * confirmação, "Sobre a empresa" e a ficha (cadastro, contatos, atribuições, negociações e histórico).
 *
 * Ajustes de 27/10 (trilha cadastros): responsável como avatar e o filtro de vários responsáveis com busca, predefinidos com
 * os grupos Vendas e Negociações, filtros do RD (nome, períodos, pausadas, filtros salvos; C14, C22); na ficha, o menu "mais"
 * com "Excluir empresa" (C7), a edição rápida de cada campo, campos personalizados e contatos que expandem (C12, C20), os
 * cards de valor coloridos (C11) e a aba Negociações com qualificação, status, próxima tarefa e paginação (C10).
 */
export function Empresas() {
  return (
    <Routes>
      <Route index element={<ListaEmpresas />} />
      <Route path=":id" element={<FichaEmpresa />} />
    </Routes>
  );
}

const PRESETS_VENDAS = ['recorrentes', 'recentes', 'antigas', 'com', 'sem'] as const;
const PRESETS_NEGOCIACOES = ['andamento', 'contato_recente', 'sem_negociacao'] as const;
const QUANTIDADES = ['total', 'abertas', 'ganhas', 'perdidas', 'pausadas'] as const;
const PERIODOS = ['criado', 'ultimo_contato', 'ultima_venda'] as const;
const FILTROS_DO_PAINEL = [
  'nome',
  ...PERIODOS.flatMap((c) => [`${c}_preset`, `${c}_desde`, `${c}_ate`]),
  'setor_id',
  'endereco',
  'seguidor_id',
  ...QUANTIDADES.flatMap((q) => [`neg_${q}_min`, `neg_${q}_max`]),
];
export const TELA_FILTROS_EMPRESAS = 'cadastros.crm-empresas-filtros';

type Filtros = Record<string, string>;
const limpos = (f: Filtros) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v !== undefined)) as Filtros;

/** Responsável principal e adicionais (C13): avatares com o nome no title. */
function Responsaveis({ p }: { p: Pick<ParceiroCrm, 'responsavel_nome' | 'responsaveis_adicionais' | 'responsavel_todos'> }) {
  const { t } = useTranslation('cadastros');
  const nomes = [p.responsavel_nome, ...(p.responsaveis_adicionais ?? []).map((r) => r.nome)].filter((x): x is string => !!x);
  if (!nomes.length && !p.responsavel_todos) return null;
  // Lista de 30/10 (B), item 3: a empresa sem carteira separada mostra "Todos" antes dos avatares.
  return (
    <span className="flex items-center gap-2">
      {p.responsavel_todos && (
        <span data-responsavel-todos="">
          <Selo>{t('crm.lbEmpresa.todos')}</Selo>
        </span>
      )}
      {nomes.length > 0 && (
        <span className="flex -space-x-1">
          {nomes.slice(0, 3).map((n, i) => (
            <Iniciais key={`${n}-${i}`} nome={n} />
          ))}
          {nomes.length > 3 && <span className="ml-2 self-center text-xs text-tinta-3">+{nomes.length - 3}</span>}
        </span>
      )}
    </span>
  );
}

/**
 * Filtro de responsáveis (C14), como no RD: "Todas as empresas", "Minhas empresas" ou "N responsáveis", com busca e marcação
 * de vários (inclui "Sem responsável").
 */
function FiltroResponsaveis({ valor, aoMudar, membros }: { valor: string; aoMudar: (v: string) => void; membros: { valor: string; rotulo: string }[] }) {
  const { t } = useTranslation('cadastros');
  const s = useSessao();
  const [busca, setBusca] = useState('');
  const ids = valor ? valor.split(',') : [];
  const texto = !ids.length ? t('crm.empresas.todas') : ids.length === 1 && ids[0] === s.usuario.id ? t('crm.empresas.minhas') : t('crm.empresas.responsaveis', { count: ids.length });
  const marcar = (id: string, sim: boolean) => aoMudar((sim ? [...ids, id] : ids.filter((x) => x !== id)).join(','));
  const opcoes = [{ valor: s.usuario.id, rotulo: t('crm.empresas.minhas') }, { valor: 'sem', rotulo: t('crm.empresas.semResponsavel') }, ...membros.filter((m) => m.valor !== s.usuario.id)];
  const visiveis = opcoes.filter((o) => !busca.trim() || o.rotulo.toLowerCase().includes(busca.trim().toLowerCase()));
  return (
    <Popover rotulo={t('crm.campos.responsavel')} icone={<Users className="size-4" />} texto={texto} classePainel="left-0 w-72" data-filtro-responsavel="">
      <div className="flex flex-col gap-2">
        <Entrada aria-label={t('crm.aj.buscarResponsavel')} placeholder={t('crm.aj.buscar')} value={busca} onChange={(e) => setBusca(e.target.value)} />
        <ul className="flex max-h-72 flex-col overflow-y-auto">
          {visiveis.map((o) => (
            <li key={o.valor}>
              <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded px-2 text-sm hover:bg-sup-3 md:min-h-9">
                <input type="checkbox" checked={ids.includes(o.valor)} onChange={(e) => marcar(o.valor, e.target.checked)} />
                <span className="min-w-0 truncate" title={o.rotulo}>{o.rotulo}</span>
              </label>
            </li>
          ))}
        </ul>
        {ids.length > 0 && (
          <Botao tamanho="sm" variante="fantasma" onClick={() => aoMudar('')}>
            {t('crm.empresas.todas')}
          </Botao>
        )}
      </div>
    </Popover>
  );
}

function ListaEmpresas() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const membros = useMembros();
  const setores = useSetores();
  const podeNegociar = usePodeNegociar();
  const aviso = useAvisoEmMassa();
  const toast = useToast();
  const [f, setF] = useState<Filtros>({});
  const [painel, setPainel] = useState(false);
  const [criar, setCriar] = useState(false);
  const [importar, setImportar] = useState(false);
  const [sobre, setSobre] = useState<ParceiroCrm | null>(null);
  const [acao, setAcao] = useState<null | 'transferir' | 'setor' | 'seguidor' | 'arquivar'>(null);
  const [valor, setValor] = useState('');
  const [negociar, setNegociar] = useState<ParceiroCrm[] | null>(null);
  const [marcandoTodos, setMarcandoTodos] = useState(false);
  const [reativar, setReativar] = useState<string[] | null>(null);
  const [reativando, setReativando] = useState(false);
  const sel = useSelecaoCrm<ParceiroCrm>((p) => nomePessoa(p.nome));
  // crm5 (E1): a lista "Inativas" (arquivadas que podem voltar), com "Reativar" em vez das ações de venda.
  const inativas = f.inativos === 'true';
  const podeReativar = inativas && s.pode('cadastros.editar');
  const params = { papel: 'empresa', com_resumo: true, ...paramsDosFiltros(f) };
  const noPainel = contarFiltros(f, FILTROS_DO_PAINEL);
  const nEmp = (n: number) => t('crm.n.empresa', { count: n });
  const opcoesMembros = (membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }));
  const mudar = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));

  const colunas: Coluna<ParceiroCrm>[] = [
    { chave: 'nome', rotulo: t('crm.campos.empresa'), ordenavel: true, principal: true, render: (p) => <span className="font-medium text-azul">{nomePessoa(p.nome)}</span> },
    { chave: 'responsavel_nome', rotulo: t('crm.campos.responsavel'), render: (p) => <Responsaveis p={p} /> },
    { chave: 'setor_nome', rotulo: t('crm.campos.segmento'), render: (p) => [p.setor_nome, ...(p.setores_adicionais ?? []).map((x) => x.nome)].filter(Boolean).join(', ') },
    { chave: 'negociacoes', rotulo: t('crm.campos.negociacoes'), alinhar: 'direita', mono: true, render: (p) => numero(p.comercial?.total ?? 0) },
    { chave: 'ultimo_contato', rotulo: t('crm.campos.ultimoContato'), render: (p) => (p.comercial?.ultimo_contato ? dataHora(p.comercial.ultimo_contato) : <span className="text-tinta-3">{t('crm.campos.naoRealizado')}</span>) },
    {
      chave: 'sobre',
      rotulo: t('crm.empresas.sobre'),
      render: (p) => (
        <button
          type="button"
          data-sobre=""
          aria-label={`${t('crm.empresas.sobre')}: ${nomePessoa(p.nome)}`}
          title={t('crm.empresas.sobre')}
          onClick={(e) => {
            e.stopPropagation();
            setSobre(p);
          }}
          className="flex size-11 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 md:size-8"
        >
          <Info className="size-4" aria-hidden />
        </button>
      ),
    },
    { chave: 'site', rotulo: t('crm.campos.site'), oculta: true, render: (p) => p.site ?? '' },
    { chave: 'created_at', rotulo: t('crm.campos.criadoEm'), ordenavel: true, oculta: true, mono: true, render: (p) => data(p.created_at) },
    ...(podeReativar
      ? [
          {
            chave: 'reativar',
            rotulo: t('crm.inativar.reativar'),
            // RCRM5 VIS-B2: no celular o botão fica ao lado do cartão (o cartão só mostra 3 colunas).
            acaoNoCartao: true,
            render: (p: ParceiroCrm) => (
              <Botao
                tamanho="sm"
                icone={<ArchiveRestore className="size-4" />}
                aria-label={`${t('crm.inativar.reativar')}: ${nomePessoa(p.nome)}`}
                data-reativar=""
                onClick={(e) => {
                  e.stopPropagation();
                  setReativar([p.id]);
                }}
              >
                {t('crm.inativar.reativar')}
              </Botao>
            ),
          } satisfies Coluna<ParceiroCrm>,
        ]
      : []),
  ];
  // RCRM5-ROB-M2: o resultado de uma ação em massa: só os feitos saem da seleção; com um bloco caído, o aviso "X de N".
  const avisar = async (r: Awaited<ReturnType<typeof emMassa>>) => {
    sel.tirar(r.feitos);
    if (r.parcial === undefined) return aviso(r);
    await aviso({ alterados: 0, falhas: [] });
    toast.erro(t('crm.inativar.massaParcial', { feitos: r.feitos.length, total: r.total, motivo: r.parcial }));
  };
  const confirmarReativar = async (ids: string[]) => {
    // Reativar em massa pela rota em massa (teto de 50 por chamada, em blocos), como o Inativar desta tela.
    setReativando(true);
    try {
      await avisar(await emMassa(ids, 'desarquivar', null, new Set(ids).size));
      setReativar(null);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setReativando(false);
    }
  };

  const confirmar = async (confirmacao: number) => {
    try {
      await avisar(await emMassa(sel.ids, acao!, acao === 'arquivar' ? null : valor || null, confirmacao));
      setAcao(null);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  const exportar = async () => {
    try {
      const itens = await registrosPorIds<ParceiroCrm>(sel.ids);
      baixarCsv('empresas.csv', [
        [t('crm.campos.empresa'), t('crm.campos.segmento'), t('crm.campos.site'), t('crm.campos.responsavel'), t('crm.campos.negociacoes')],
        ...itens.map((p) => [p.nome, p.setor_nome, p.site, p.responsavel_nome, p.comercial?.total ?? 0]),
      ]);
      toast.sucesso(t('crm.massa.exportado'));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  const nomeMembro = (id: string) => nomePessoa(membros.data?.itens.find((m) => m.id === id)?.nome ?? '');
  const textosAcao = {
    transferir: { titulo: t('crm.massa.transferirTitulo'), texto: t('crm.massa.transferirTexto', { n: nEmp(sel.n), nome: valor ? nomeMembro(valor) : t('crm.empresas.semResponsavel') }), botao: t('crm.massa.transferirBotao') },
    setor: { titulo: t('crm.massa.segmentoTitulo'), texto: t('crm.massa.segmentoTexto', { n: nEmp(sel.n) }), botao: t('crm.massa.segmentoBotao') },
    seguidor: { titulo: t('crm.massa.seguidorTitulo'), texto: t('crm.massa.seguidorTexto', { n: nEmp(sel.n) }), botao: t('crm.massa.seguidorBotao') },
    arquivar: { titulo: t('crm.inativar.massaTitulo'), texto: t('crm.inativar.massaTexto', { n: nEmp(sel.n) }), botao: t('crm.inativar.acao') },
  };
  // Predefinidos com os grupos do RD (Vendas e Negociações): o título do grupo é uma opção que não se escolhe.
  const opcoesPredefinidos = [
    { valor: '_vendas', rotulo: t('crm.filtros.grupoVendas'), desabilitado: true },
    ...PRESETS_VENDAS.map((p) => ({ valor: p, rotulo: t(`crm.filtros.vendas.${p}`) })),
    { valor: '_negociacoes', rotulo: t('crm.filtros.grupoNegociacoes'), desabilitado: true },
    ...PRESETS_NEGOCIACOES.map((p) => ({ valor: p, rotulo: t(`crm.filtros.vendas.${p}`) })),
  ];

  return (
    <div data-pagina-crm="empresas">
      <CabecalhoPagina titulo={t('crm.empresas.titulo')}>
        {/* C15: atalho de tarefas (ligação das trilhas, publicado pelo Pipeline). */}
        <AtalhoDeTarefas />
        {s.pode('cadastros.importar') && s.pode('pipeline.criar') && (
          <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
            {t('acoes.importar')}
          </Botao>
        )}
        {s.pode('cadastros.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setCriar(true)}>
            {t('crm.empresas.criar')}
          </Botao>
        )}
      </CabecalhoPagina>
      <BarraSelecao
        n={sel.n}
        aoLimpar={sel.limpar}
        todos={{
          rotulo: t('crm.empresas.selecionarTodas'),
          carregando: marcandoTodos,
          aoMarcar: async () => {
            setMarcandoTodos(true);
            try {
              sel.marcarTodos(await idsDoFiltro(params));
            } finally {
              setMarcandoTodos(false);
            }
          },
        }}
      >
        {podeReativar && (
          <Botao tamanho="sm" icone={<ArchiveRestore className="size-4" />} onClick={() => setReativar(sel.ids)} data-reativar-massa="">
            {t('crm.inativar.reativar')}
          </Botao>
        )}
        {!inativas && s.pode('cadastros.editar') && (
          <>
            <Botao tamanho="sm" onClick={() => (setValor(''), setAcao('transferir'))}>
              {t('crm.acoes.transferir')}
            </Botao>
            <Botao tamanho="sm" onClick={() => (setValor(''), setAcao('setor'))}>
              {t('crm.campos.segmento')}
            </Botao>
            <Botao tamanho="sm" onClick={() => (setValor(''), setAcao('seguidor'))}>
              {t('crm.campos.seguidor')}
            </Botao>
          </>
        )}
        {!inativas && podeNegociar && (
          <Botao tamanho="sm" onClick={async () => setNegociar(await registrosPorIds<ParceiroCrm>(sel.ids))}>
            {t('crm.acoes.criarNegociacao')}
          </Botao>
        )}
        {/* Val5 (29/10): exportar tem ação própria (`cadastros.exportar`); arquivar (inativar) em massa, `cadastros.arquivar` (a rota pede também `cadastros.editar`). */}
        {s.pode('cadastros.exportar') && (
          <Botao tamanho="sm" onClick={() => void exportar()}>
            {t('crm.acoes.exportar')}
          </Botao>
        )}
        {!inativas && s.pode('cadastros.arquivar') && s.pode('cadastros.editar') && (
          <Botao tamanho="sm" variante="perigo" onClick={() => setAcao('arquivar')} data-inativar-massa="">
            {t('crm.inativar.acao')}
          </Botao>
        )}
      </BarraSelecao>
      <Tabela<ParceiroCrm>
        tela="cadastros.crm-empresas"
        caminho="/cadastros/parceiros"
        params={params}
        chaveConsulta={['crm', 'empresas']}
        filtros={f}
        aoAplicarFiltros={(x) => setF(Object.fromEntries(Object.entries(x).map(([k, v]) => [k, String(v)])))}
        buscaNaUrl
        filtrosExtra={
          <>
            <FiltroResponsaveis valor={f.responsavel_ids ?? ''} aoMudar={(v) => mudar('responsavel_ids', v)} membros={opcoesMembros} />
            <div className="w-full sm:w-40">
              <Selecao
                aria-label={t('crm.inativar.situacao')}
                data-filtro-situacao=""
                value={f.inativos ?? ''}
                onChange={(e) => {
                  sel.limpar();
                  mudar('inativos', e.target.value);
                }}
                vazio={t('crm.inativar.ativas')}
                opcoes={[{ valor: 'true', rotulo: t('crm.inativar.inativas') }]}
              />
            </div>
            {/* crm8 (2): lead (só no CRM) ou cliente (já ganhou). */}
            <div className="w-full sm:w-40">
              <Selecao
                aria-label={t('crm.fase.rotulo')}
                data-filtro-fase=""
                value={f.fase ?? ''}
                onChange={(ev) => mudar('fase', ev.target.value)}
                vazio={t('crm.fase.todas')}
                opcoes={(['lead', 'cliente'] as const).map((x) => ({ valor: x, rotulo: t(`crm.fase.${x}`) }))}
              />
            </div>
            <div className="w-full sm:w-64">
              <Selecao aria-label={t('crm.filtros.predefinidos')} data-filtro-vendas="" value={f.vendas ?? ''} onChange={(e) => mudar('vendas', e.target.value)} vazio={t('crm.filtros.predefinidos')} opcoes={opcoesPredefinidos} />
            </div>
            <Botao icone={<Filter className="size-4" />} onClick={() => setPainel(true)} data-abrir-filtros="">
              {t('crm.filtros.botao', { n: noPainel })}
            </Botao>
          </>
        }
        selecao={sel.selecao}
        colunas={colunas}
        entidade="cadastros.parceiro"
        aoClicar={(p) => navegar(`${CAMINHO_EMPRESAS}/${p.id}`)}
        vazio={<Vazio texto={inativas ? t('crm.inativar.vazio') : t('crm.empresas.vazio')} />}
      />
      <PainelFiltros
        aberto={painel}
        aoFechar={() => setPainel(false)}
        atual={f}
        membros={opcoesMembros}
        setores={(setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))}
        aoAplicar={(novo) => {
          setF((x) => ({ ...Object.fromEntries(Object.entries(x).filter(([k]) => !FILTROS_DO_PAINEL.includes(k))), ...novo }));
          setPainel(false);
        }}
      />
      {acao && (
        <ConfirmarContagem
          aberto
          aoFechar={() => setAcao(null)}
          titulo={textosAcao[acao].titulo}
          texto={textosAcao[acao].texto}
          botao={textosAcao[acao].botao}
          perigoso={acao === 'arquivar'}
          n={sel.n}
          podeConfirmar={acao === 'transferir' || acao === 'arquivar' || !!valor}
          aoConfirmar={confirmar}
        >
          {acao !== 'arquivar' && (
            <Selecao
              aria-label={acao === 'transferir' ? t('crm.campos.responsavel') : acao === 'setor' ? t('crm.campos.segmento') : t('crm.campos.seguidor')}
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              vazio={acao === 'transferir' ? t('crm.empresas.semResponsavel') : tn('comum.selecione')}
              opcoes={acao === 'setor' ? (setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome })) : opcoesMembros}
            />
          )}
        </ConfirmarContagem>
      )}
      {sobre && (
        <GavetaSobre
          aberto
          aoFechar={() => setSobre(null)}
          titulo={t('crm.empresas.sobre')}
          abrir={{ rotulo: t('crm.empresas.abrir'), para: `${CAMINHO_EMPRESAS}/${sobre.id}` }}
          linhas={[
            [t('crm.campos.segmento'), [sobre.setor_nome, ...(sobre.setores_adicionais ?? []).map((x) => x.nome)].filter(Boolean).join(', ') || t('crm.campos.naoHa')],
            [t('crm.campos.site'), sobre.site || t('crm.campos.naoHa')],
            [t('crm.campos.resumo'), textoLivre(sobre.observacoes, sobre.nome) || t('crm.campos.naoHa')],
          ]}
        />
      )}
      <Confirmar
        aberto={!!reativar}
        aoFechar={() => setReativar(null)}
        titulo={t('crm.inativar.reativarTitulo')}
        texto={t('crm.inativar.reativarTexto', { n: nEmp(reativar?.length ?? 0) })}
        carregando={reativando}
        aoConfirmar={() => void confirmarReativar(reativar ?? [])}
      />
      {negociar && <CriarNegociacaoEmLote parceiros={negociar} aoFechar={() => setNegociar(null)} aoCriar={sel.limpar} />}
      <FormEmpresa
        aberto={criar}
        aoFechar={() => {
          setCriar(false);
        }}
        aoSalvar={(id) => navegar(`${CAMINHO_EMPRESAS}/${id}`)}
        criarOutra
      />
      {/* Rodada rlote-5 (SPEC-M1): pelo CRM, os criados nascem lead (fora da operação até o ganho). */}
      <ImportarCsv crm aberto={importar} aoFechar={() => setImportar(false)} />
    </div>
  );
}

/** O painel "Filtros" do RD (C14): filtros salvos, nome, períodos, segmento, endereço, seguidores e as quantidades de negociações. */
function PainelFiltros({ aberto, aoFechar, atual, membros, setores, aoAplicar }: { aberto: boolean; aoFechar: () => void; atual: Filtros; membros: { valor: string; rotulo: string }[]; setores: { valor: string; rotulo: string }[]; aoAplicar: (f: Filtros) => void }) {
  const { t } = useTranslation('cadastros');
  const id = useId();
  const [f, setF] = useState<Filtros>(atual);
  const [aberturaAnterior, setAberturaAnterior] = useState(aberto);
  if (aberto !== aberturaAnterior) {
    setAberturaAnterior(aberto);
    if (aberto) setF(atual);
  }
  const mudar = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));
  const campo = (k: string) => ({ value: f[k] ?? '', onChange: (e: { target: { value: string } }) => mudar(k, e.target.value) });
  const doPainel = (x: Filtros) => limpos(Object.fromEntries(FILTROS_DO_PAINEL.map((k) => [k, x[k] ?? ''])));
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('crm.filtros.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={() => aoAplicar({})}>
            {t('crm.filtros.limpar')}
          </Botao>
          <Botao variante="primario" data-aplicar-filtros="" onClick={() => aoAplicar(doPainel(f))}>
            {t('crm.filtros.aplicar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FiltrosSalvos tela={TELA_FILTROS_EMPRESAS} filtros={doPainel(f)} aoAplicar={(x) => aoAplicar(doPainel(x))} />
        <p className="kicker">{t('crm.aj.selecionarFiltros')}</p>
        <Rotulo id={`${id}-nome`} rotulo={t('crm.aj.nomeDaEmpresa')}>
          <Entrada id={`${id}-nome`} data-filtro-nome="" placeholder={t('crm.aj.filtrarPorNome')} {...campo('nome')} />
        </Rotulo>
        <FiltroPeriodo campo="criado" rotulo={t('crm.campos.criadoEm')} f={f} mudar={mudar} />
        <FiltroPeriodo campo="ultimo_contato" rotulo={t('crm.aj.dataUltimoContato')} f={f} mudar={mudar} />
        <FiltroPeriodo campo="ultima_venda" rotulo={t('crm.aj.dataUltimaVenda')} f={f} mudar={mudar} />
        <Rotulo id={`${id}-setor`} rotulo={t('crm.campos.segmento')}>
          <Selecao id={`${id}-setor`} {...campo('setor_id')} vazio={t('crm.filtros.todos')} opcoes={setores} />
        </Rotulo>
        <Rotulo id={`${id}-end`} rotulo={t('crm.campos.endereco')}>
          <Entrada id={`${id}-end`} placeholder={t('crm.aj.filtrarPorEndereco')} {...campo('endereco')} />
        </Rotulo>
        <Rotulo id={`${id}-seg`} rotulo={t('crm.campos.seguidores')}>
          <Selecao id={`${id}-seg`} {...campo('seguidor_id')} vazio={t('crm.filtros.todos')} opcoes={membros} />
        </Rotulo>
        {QUANTIDADES.map((q) => (
          <fieldset key={q} className="flex flex-col gap-1">
            <legend className="text-sm font-medium text-tinta-2">{t(`crm.filtros.quantidade.${q}`)}</legend>
            <div className="flex gap-3">
              <Entrada aria-label={`${t(`crm.filtros.quantidade.${q}`)}: ${t('crm.filtros.de')}`} placeholder={t('crm.filtros.de')} inputMode="numeric" {...campo(`neg_${q}_min`)} />
              <Entrada aria-label={`${t(`crm.filtros.quantidade.${q}`)}: ${t('crm.filtros.ate')}`} placeholder={t('crm.filtros.ate')} inputMode="numeric" {...campo(`neg_${q}_max`)} />
            </div>
          </fieldset>
        ))}
      </div>
    </Drawer>
  );
}

interface Negociacao {
  id: string;
  titulo: string;
  /** Lista de 03/11, item 3: a empresa principal da negociação (diferente da desta página, esta é adicional). */
  parceiro_id: string;
  /** Rodada rcn-3: o nome dela, para o modo discreto censurar o título que a cita. */
  parceiro_nome?: string | null;
  etapa_nome: string;
  funil_nome: string | null;
  valor_centavos: number;
  moeda: string;
  responsavel_nome: string | null;
  estado: 'aberta' | 'ganha' | 'perdida';
  pausada_em: string | null;
  qualificacao: number;
  ganha_em: string | null;
  created_at: string;
  /** Em andamento na primeira etapa do funil: o selo "Nova" (crm4 L3). */
  primeira_etapa: boolean;
  proxima_tarefa: { titulo: string; data_hora: string } | null;
}

/** Uma linha de `GET /pipeline/crm/negociacoes` no formato da ficha (estado, pausa e ganho em vez do status do funil). */
interface NegociacaoCrmApi extends Omit<Negociacao, 'estado' | 'pausada_em' | 'ganha_em'> {
  status: 'andamento' | 'vendido' | 'perdido' | 'pausado';
  fechada_em: string | null;
}
const daApi = (n: NegociacaoCrmApi): Negociacao => ({
  ...n,
  estado: n.status === 'vendido' ? 'ganha' : n.status === 'perdido' ? 'perdida' : 'aberta',
  pausada_em: n.status === 'pausado' ? n.created_at : null,
  ganha_em: n.status === 'vendido' ? n.fechada_em : null,
});

/** Soma por moeda (nunca entre moedas). */
function somaPorMoeda(lista: Pick<Negociacao, 'moeda' | 'valor_centavos'>[]) {
  const m = new Map<string, number>();
  for (const n of lista) m.set(n.moeda, (m.get(n.moeda) ?? 0) + Number(n.valor_centavos));
  return m.size ? [...m].map(([moeda, v]) => dinheiro(v, moeda)).join(' · ') : dinheiro(0);
}

const POR_PAGINA = 10;

/** Qualificação em estrelas (0 a 5), com o texto para leitor de tela. */
function Estrelas({ n }: { n: number }) {
  const { t } = useTranslation('cadastros');
  return (
    <span className="whitespace-nowrap text-azul" title={t('crm.aj.estrelas', { n })}>
      <span aria-hidden className="inline-flex gap-px align-middle">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} className={`size-3.5 ${i <= n ? 'fill-current' : 'text-tinta-3'}`} />
        ))}
      </span>
      <span className="sr-only">{t('crm.aj.estrelas', { n })}</span>
    </span>
  );
}

/** A aba Negociações da empresa (C10): a tabela do RD com qualificação, status, próxima tarefa, "Sobre" e paginação de 10. */
function NegociacoesDaEmpresa({ lista, nomes, empresaId }: { lista: Negociacao[]; nomes: string[]; empresaId: string }) {
  const { t } = useTranslation('cadastros');
  const [pagina, setPagina] = useState(0);
  const [sobre, setSobre] = useState<Negociacao | null>(null);
  const paginas = Math.max(1, Math.ceil(lista.length / POR_PAGINA));
  const visiveis = lista.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA);
  if (!lista.length) return <p className="p-4 text-sm text-tinta-3">{t('crm.empresas.semNegociacoes')}</p>;
  return (
    <>
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm" data-negociacoes-empresa="">
          <thead>
            <tr className="border-b border-borda text-left text-xs text-tinta-3">
              <th className="px-4 py-2 font-medium">{t('crm.aj.colNegociacao')}</th>
              <th className="px-2 py-2 font-medium">{t('crm.campos.responsavel')}</th>
              <th className="px-2 py-2 font-medium">{t('crm.aj.qualificacao')}</th>
              <th className="px-2 py-2 font-medium">{t('crm.aj.colStatus')}</th>
              <th className="px-2 py-2 text-right font-medium">{t('crm.aj.valorTotal')}</th>
              <th className="px-2 py-2 font-medium">{t('crm.aj.proximaTarefa')}</th>
              <th className="px-2 py-2">
                <span className="sr-only">{t('crm.aj.sobreNegociacao')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borda">
            {visiveis.map((n) => {
              const st = statusDaNegociacao(n);
              const prox = n.proxima_tarefa;
              return (
                <tr key={n.id}>
                  <td className="px-4 py-2">
                    <Link to={caminhoDaNegociacaoCrm(n.id)} className="font-semibold text-azul">
                      {textoLivre(n.titulo, ...nomes, n.parceiro_nome)}
                    </Link>
                    <span className="block text-xs text-tinta-3">{rotuloEtapaPipeline(n.etapa_nome)}</span>
                    {/* Lista de 03/11, item 3: a negociação em que esta empresa não é a principal. */}
                    {n.parceiro_id !== empresaId && (
                      <span className="mt-0.5 block" data-empresa-adicional-na-negociacao="">
                        <Selo cor="neutro">{t('crm.aj.empresaAdicional')}</Selo>
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <Iniciais nome={n.responsavel_nome} />
                  </td>
                  <td className="px-2 py-2">
                    <Estrelas n={n.qualificacao ?? 0} />
                  </td>
                  <td className="px-2 py-2">
                    <Selo cor={st.cor}>{t(`crm.aj.status.${st.chave}`)}</Selo>
                  </td>
                  <td className="numero px-2 py-2 text-right">{dinheiro(n.valor_centavos, n.moeda)}</td>
                  <td className="px-2 py-2">{prox ? textoLivre(prox.titulo, ...nomes, n.parceiro_nome) : <span className="text-tinta-3">{t('crm.aj.semTarefas')}</span>}</td>
                  <td className="px-2 py-2">
                    <button type="button" aria-label={`${t('crm.aj.sobreNegociacao')}: ${textoLivre(n.titulo, ...nomes, n.parceiro_nome)}`} title={t('crm.aj.sobreNegociacao')} onClick={() => setSobre(n)} className="flex size-11 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 md:size-8">
                      <Info className="size-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-borda px-4 py-2 text-sm text-tinta-3">
        <span>{t('crm.aj.exibindo', { n: visiveis.length, total: lista.length })}</span>
        <span className="flex items-center gap-2">
          <Botao tamanho="sm" variante="fantasma" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
            {t('crm.aj.anterior')}
          </Botao>
          <span className="numero font-semibold text-tinta-1">{pagina + 1}</span>
          <Botao tamanho="sm" variante="fantasma" disabled={pagina >= paginas - 1} onClick={() => setPagina((p) => p + 1)}>
            {t('crm.aj.proximo')}
          </Botao>
        </span>
      </div>
      {sobre && (
        <GavetaSobre
          aberto
          aoFechar={() => setSobre(null)}
          titulo={t('crm.aj.sobreNegociacao')}
          abrir={{ rotulo: t('crm.aj.abrirNegociacao'), para: caminhoDaNegociacaoCrm(sobre.id) }}
          linhas={[
            [t('crm.aj.funil'), sobre.funil_nome ? rotuloFunilPipeline(sobre.funil_nome) : t('crm.campos.naoPreenchido')],
            [t('crm.aj.etapa'), rotuloEtapaPipeline(sobre.etapa_nome)],
            [t('crm.aj.colStatus'), t(`crm.aj.status.${statusDaNegociacao(sobre).chave}`)],
            [t('crm.campos.responsavel'), sobre.responsavel_nome ? nomePessoa(sobre.responsavel_nome) : t('crm.campos.naoPreenchido')],
            [t('crm.aj.valorTotal'), dinheiro(sobre.valor_centavos, sobre.moeda)],
            [t('crm.campos.criadoEm'), data(sobre.created_at)],
          ]}
        />
      )}
    </>
  );
}

/** Seção que abre e fecha (Cadastro, Observações), como no RD. */
function Secao({ titulo, aberta, aoAlternar, children }: { titulo: string; aberta: boolean; aoAlternar: () => void; children: ReactNode }) {
  const id = useId();
  return (
    <div>
      <h4>
        <button type="button" aria-expanded={aberta} aria-controls={id} onClick={aoAlternar} className="flex min-h-11 w-full items-center justify-between rounded-lg px-2 text-sm font-semibold text-tinta-1 hover:bg-sup-2 md:min-h-9">
          {titulo}
          {aberta ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />}
        </button>
      </h4>
      {aberta && <div id={id}>{children}</div>}
    </div>
  );
}

/**
 * Lista de 30/10 (B), item 3.1 (b): o endereço do LinkedIn para o botão, sempre https. Aceita o endereço inteiro (http ou
 * https), o endereço sem o esquema (linkedin.com/in/...) ou só o perfil (vira linkedin.com/in/<perfil>); outro esquema não vira link.
 */
export function urlDoLinkedin(v: string): string | null {
  const x = v.trim();
  if (!x) return null;
  let url: URL;
  try {
    url = /^https?:\/\//i.test(x) ? new URL(x) : /^[a-z][a-z0-9+.-]*:/i.test(x) ? new URL('invalido:') : /linkedin\.com/i.test(x) ? new URL(`https://${x.replace(/^\/+/, '')}`) : new URL(`https://www.linkedin.com/in/${encodeURIComponent(x.replace(/^@+/, ''))}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  url.protocol = 'https:';
  return url.toString();
}

export { intercalarHistorico };

/**
 * Lista de 30/10 (B), item 3.1 (a): a aba Histórico da empresa com tudo o que a envolve no sistema. A linha do tempo da
 * própria empresa (com anotação, menção e anexo) recebe, misturados na ordem do tempo, os eventos dos contatos, das negociações
 * (atividades, e-mails, propostas, etapas) e das tarefas ligadas, com os arquivos anexados a eles, cada um com a origem. A
 * leitura é do `GET /chatter-agregado`, que confere de novo permissão, empresa e a regra de cada módulo.
 */
function HistoricoEmpresa({ e, negociacoes }: { e: FichaParceiro & ParceiroCrm; negociacoes: { id: string; titulo: string; parceiro_nome?: string | null }[] }) {
  const { t } = useTranslation('cadastros');
  // Lista de 02/11, item 6: o "Criar tarefa" do histórico abre o mesmo formulário do botão flutuante, com a empresa.
  const criacaoTarefa = useCriacaoDaChave('tarefa');
  const criacao = useAbrirCriacao();
  // Rodada rcn-4 (SEG-M1): o titular vem da lista da ficha, não só dos vínculos, que cortam em 50 e podem falhar.
  const nomes = [e.nome, ...e.contatos.map((c) => c.nome), ...negociacoes.map((n) => n.parceiro_nome)];
  const doNegocio: RegistroDoHistorico[] = negociacoes.map((n) => ({ entidade: 'pipeline.oportunidade', id: n.id, origem: <OrigemDoHistorico texto={t('crm.lbEmpresa.origem.negociacao', { nome: textoLivre(n.titulo, ...nomes, n.parceiro_nome) })} link={caminhoDaNegociacaoCrm(n.id)} /> }));
  const dosContatos: RegistroDoHistorico[] = e.contatos.map((c) => ({ entidade: 'cadastros.parceiro', id: c.id, origem: <OrigemDoHistorico texto={t('crm.lbEmpresa.origem.contato', { nome: nomePessoa(c.nome) })} link={`${CAMINHO_CONTATOS}/${c.id}`} /> }));
  const { relacionados, cortado, nomes: nomesDoHistorico } = useHistoricoDoParceiro({ chave: ['crm', 'empresa', e.id], parceiroId: e.id, nomes, proprios: [doNegocio, dosContatos] });
  return (
    <div className="flex flex-col gap-3" data-historico-empresa="" data-relacionados={relacionados.length}>
      <p className="text-sm text-tinta-3">{t('crm.lbEmpresa.historicoAjuda')}</p>
      {cortado && (
        <p className="text-sm text-tinta-2" role="status" data-historico-cortado="">
          {cortado}
        </p>
      )}
      <DialogoCriacao aberta={criacao.aberta} aoFechar={criacao.fechar} contexto={{ empresa: { id: e.id, nome: e.nome } }} />
      <Chatter variante="linha" criarTarefa={criacaoTarefa ? () => criacao.abrir(criacaoTarefa) : null} marco={{ rotulo: t('crm.aj.empresaCriada'), data: e.created_at }} entidade="cadastros.parceiro" registroId={e.id} titulo={e.nome} nomes={nomesDoHistorico} relacionados={relacionados} />
    </div>
  );
}

function FichaEmpresa() {
  const { id = '' } = useParams();
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const membros = useMembros();
  const setores = useSetores();
  const podeNegociar = usePodeNegociar();
  const extrasDefs = useCamposPersonalizados('cadastros.parceiro');
  const [aba, setAba] = useState<'negociacoes' | 'historico'>('negociacoes');
  const [editar, setEditar] = useState(false);
  const [novoContato, setNovoContato] = useState(false);
  const [adicionarContato, setAdicionarContato] = useState(false);
  const [negociar, setNegociar] = useState(false);
  const [inativar, setInativar] = useState(false);
  const [reativar, setReativar] = useState(false);
  const [seguidor, setSeguidor] = useState('');
  const [secoes, setSecoes] = useState({ cadastro: true, sobre: true });
  const [contatoAberto, setContatoAberto] = useState<string | null>(null);
  const idAbas = useId();
  const q = useQuery({ queryKey: ['crm', 'empresa', id], queryFn: () => get<FichaParceiro & ParceiroCrm>(`/cadastros/parceiros/${id}`) });
  const sub = useSubsistema();
  const veNegociacoes = s.instalado('pipeline') && s.pode('pipeline.ver');
  const negs = useQuery({ queryKey: ['crm', 'empresa', id, 'negociacoes'], queryFn: async () => (await get<{ itens: NegociacaoCrmApi[] }>('/pipeline/crm/negociacoes', { parceiro_id: id, todos_funis: true, incluir_adicionais: true, limite: 100 })).itens.map(daApi), enabled: veNegociacoes });
  const feedback = useFeedback();
  // C8: o formulário único de negociação (publicado pelo Pipeline), com a empresa já escolhida.
  const criarNegociacao = useCriacaoDaChave('negociacao');
  const criacao = useAbrirCriacao();
  // Botão flutuante (C6): o que nasce aqui já vem com a empresa.
  useContextoDaCriacao(q.data ? { empresa: { id, nome: q.data.nome } } : {});
  useAvisoDeReleitura(q);
  // Rodada rcn-4 (ROB-M1): releitura que falha com dado em cache não desmonta a ficha, nem o formulário aberto nela.
  if (!q.data || registroInacessivel(q.error)) return q.isError ? <Erro erro={q.error} aoTentar={() => q.refetch()} /> : <Carregando />;
  const e = q.data;
  const observacoesCampo = e.observacoes ?? '';
  const lista = negs.data ?? [];
  // Lista de 03/11, item 3 (rl0311-1 SPEC-M1): a tabela mostra todas; indicadores e o aviso de exclusão seguem só a principal.
  const proprias = lista.filter((n) => n.parceiro_id === e.id);
  const ganhas = proprias.filter((n) => n.estado === 'ganha');
  const diasAteVenda = ganhas.length ? Math.round(ganhas.reduce((a, n) => a + (Date.parse(n.ganha_em!) - Date.parse(n.created_at)) / 86_400_000, 0) / ganhas.length) : 0;
  // RCRM5 SPEC-B1: a empresa inativa não se edita nem recebe negociação na ficha (fica o selo e o "Reativar empresa").
  const podeEditar = s.pode('cadastros.editar');
  const pode = podeEditar && !e.arquivado;
  // Rlote-9 (SPEC-B3): negociação criada ou mudada na ficha atualiza também o cartão Verticais na hora (crm8 item 1).
  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['crm', 'empresa', id] }), qc.invalidateQueries({ queryKey: chaveVerticaisRelacionamento(id) })]);
  const editarCampo = async (corpo: Record<string, unknown>) => {
    await patch(`/cadastros/parceiros/${id}`, corpo);
    await recarregar();
    await qc.invalidateQueries({ queryKey: ['crm', 'empresas'] });
    toast.sucesso(tn('comum.salvo'));
  };
  // crm4 (L4): o principal (primeiro da lista) vai no cadastro; os adicionais, pela rota própria (substitui a lista inteira).
  const editarListas = async (principal: Record<string, unknown>, adicionais: Record<string, unknown>) => {
    await patch(`/cadastros/parceiros/${id}`, principal);
    await api('PUT', `/cadastros/parceiros/${id}/adicionais`, adicionais);
    await recarregar();
    await qc.invalidateQueries({ queryKey: ['crm', 'empresas'] });
    toast.sucesso(tn('comum.salvo'));
  };
  // crm4 (L4): corrige o primeiro endereço ou cria um (principal) quando a empresa não tem.
  const editarEndereco = async (d: DadosEnderecoRapido) => {
    const corpo = Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v || null]));
    const primeiro = e.enderecos[0];
    if (primeiro) await patch(`/cadastros/parceiros/${id}/enderecos/${primeiro.id}`, corpo);
    else await post(`/cadastros/parceiros/${id}/enderecos`, { id: crypto.randomUUID(), tipo: 'principal', ...corpo });
    await recarregar();
    await qc.invalidateQueries({ queryKey: ['crm', 'empresas'] });
    toast.sucesso(tn('comum.salvo'));
  };
  const seguir = async (usuario: string, remover = false) => {
    try {
      if (remover) await del(`/cadastros/parceiros/${id}/seguidores/${usuario}`);
      else await post(`/cadastros/parceiros/${id}/seguidores`, { usuario_id: usuario });
      setSeguidor('');
      await recarregar();
    } catch (err) {
      toast.erro(mensagemDeErro(err));
    }
  };
  const linha = (rotulo: string, valor: ReactNode) => (
    <div className="flex items-start justify-between gap-3 border-b border-borda px-2 py-2 text-sm last:border-0">
      <dt className="text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-right font-medium break-words text-tinta-1">{valor || <span className="font-normal text-tinta-3">{t('crm.campos.naoPreenchido')}</span>}</dd>
    </div>
  );
  const setoresAdicionais = (e.setores_adicionais ?? []).map((x) => x.nome);
  const regimeVigente = (e.regimes ?? []).find((r) => r.vigente)?.regime ?? null;
  const opcoesSetores = (setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }));
  for (const x of [...(e.setor_id ? [{ id: e.setor_id, nome: e.setor_nome ?? '' }] : []), ...(e.setores_adicionais ?? [])]) if (!opcoesSetores.some((o) => o.valor === x.id)) opcoesSetores.push({ valor: x.id, rotulo: x.nome });

  const opcoesMembros = [{ valor: RESPONSAVEL_TODOS, rotulo: t('crm.lbEmpresa.todos') }, ...(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))];
  for (const r of [...(e.responsavel_id ? [{ id: e.responsavel_id, nome: e.responsavel_nome ?? '' }] : []), ...(e.responsaveis_adicionais ?? [])]) {
    if (!opcoesMembros.some((o) => o.valor === r.id)) opcoesMembros.push({ valor: r.id, rotulo: nomePessoa(r.nome) });
  }
  // Campos personalizados da empresa (C20). Lista de 30/10 (B), item 3.1 (c): editáveis na hora, como os fixos (WhatsApp, Porte,
  // Data de fundação, LinkedIn e os outros do workspace); o que não tem mais definição segue só em leitura.
  // Lista de 02/11 (item 3): o WhatsApp (campo personalizado) não aparece mais: virou tipo de telefone, mostrado abaixo.
  const defsEmpresa = extrasDefs.filter((d) => !EXTRAS_FORA_DO_CRM.some((k) => d.nome === `extras.${k}`) && (!d.visivel || d.visivel({ tipo: 'empresa', extras: e.extras })));
  const extrasOrfaos = linhasExtras(defsEmpresa, Object.fromEntries(Object.entries(e.extras ?? {}).filter(([k]) => !EXTRAS_FORA_DO_CRM.includes(k))), e.nome).filter((x) => !defsEmpresa.some((d) => d.nome === `extras.${x.chave}`));
  // Lista de 30/10 (B), item 3.1 (b): o botão de abrir o LinkedIn, igual ao do site (link externo, nova aba, sem referrer).
  const linkedin = typeof e.extras?.linkedin === 'string' ? urlDoLinkedin(e.extras.linkedin) : null;
  const defLinkedin = defsEmpresa.find((d) => d.nome === 'extras.linkedin');
  // Lista de 30/10 (B), item 3.1 (e): "Abrir cadastro completo" só para quem abre a página Cadastros > Pessoas e empresas (a
  // permissão, a página do papel e, com os sub-sistemas, um sub-sistema da pessoa que a tenha; o Comercial do novo desenho não).
  const veCadastroCompleto =
    s.pode('cadastros.ver') && s.paginaLiberada('cadastros', 'pessoas') && (!sub.ativo || sub.opcoes.some((o) => paginaNoSubsistema(sub.config, o, 'cadastros', 'pessoas')));
  // O menu "mais" do RD (C7), com o "Dê seu feedback" (decisão 5). crm5 (E1): "Inativar empresa" (o arquivamento, sem exclusão
  // definitiva); a inativa mostra "Reativar empresa".
  const itensMais: ItemMenu[] = [
    ...(!e.arquivado && s.pode('cadastros.arquivar') ? [{ chave: 'inativar', rotulo: t('crm.inativar.inativarEmpresa'), icone: <Archive className="size-4" aria-hidden />, aoClicar: () => setInativar(true) }] : []),
    ...(e.arquivado && !e.mesclado_em && s.pode('cadastros.arquivar') ? [{ chave: 'reativar', rotulo: t('crm.inativar.reativarEmpresa'), icone: <ArchiveRestore className="size-4" aria-hidden />, aoClicar: () => setReativar(true) }] : []),
    { chave: 'feedback', rotulo: tn('feedback.menu'), icone: ICONE_FEEDBACK, aoClicar: feedback.abrir },
  ];
  return (
    <div data-pagina-crm="empresa">
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      <CabecalhoPagina
        subtitulo={t('crm.empresas.ficha')}
        titulo={nomePessoa(e.nome)}
        voltar={
          <LinkVoltar para={CAMINHO_EMPRESAS} aria-label={tn('comum.voltar')} title={tn('comum.voltar')} className="flex size-11 items-center justify-center rounded-lg text-tinta-2 hover:bg-sup-2 md:size-9">
            <ArrowLeft className="size-5" aria-hidden />
          </LinkVoltar>
        }
      >
        {/* crm8 (2): a fase do cadastro único; o lead fica só no CRM até o ganho. */}
        <span data-selo-fase={e.situacao_comercial ?? 'cliente'}>
          <Selo cor={e.situacao_comercial === 'lead' ? 'azul' : 'ciano'}>{t(`situacoes.${e.situacao_comercial ?? 'cliente'}`)}</Selo>
        </span>
        {e.arquivado && (
          <span data-selo-inativa="">
            <Selo cor="dourado">{t('crm.inativar.selo')}</Selo>
          </span>
        )}
        {itensMais.length > 0 && <Menu rotulo={t('crm.aj.maisOpcoes')} gatilho={<MoreHorizontal className="size-5" aria-hidden />} classeGatilho="flex size-11 items-center justify-center rounded-lg text-tinta-2 hover:bg-sup-2 md:size-10" classePainel="right-0 w-56" itens={itensMais} />}
        {pode && (
          <Botao onClick={() => setEditar(true)} data-editar-empresa="">
            {t('crm.empresas.editar')}
          </Botao>
        )}
        {podeNegociar && !e.arquivado && (
          <Botao variante="primario" icone={<Handshake className="size-4" />} onClick={() => (criarNegociacao ? criacao.abrir(criarNegociacao) : setNegociar(true))}>
            {t('crm.acoes.criarNegociacao')}
          </Botao>
        )}
      </CabecalhoPagina>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-1">
          <Cartao titulo={t('crm.campos.empresa')}>
            <Secao titulo={t('crm.empresas.cadastro')} aberta={secoes.cadastro} aoAlternar={() => setSecoes((x) => ({ ...x, cadastro: !x.cadastro }))}>
              <div data-cadastro-empresa="">
                <LinhaEditavel rotulo={t('crm.campos.empresa')} obrigatorio exibir={nomePessoa(e.nome)} valor={e.nome} pode={pode} aoSalvar={(v) => editarCampo({ nome: v })} data-campo-rapido="nome" />
                <LinhaMultiplaEditavel
                  rotulo={t('crm.campos.segmento')}
                  exibir={[e.setor_nome, ...setoresAdicionais].filter(Boolean).join(', ')}
                  valores={[...(e.setor_id ? [e.setor_id] : []), ...(e.setores_adicionais ?? []).map((x) => x.id)]}
                  opcoes={opcoesSetores}
                  rotuloPrincipal={t('crm.aj.principal')}
                  pode={pode}
                  aoSalvar={(v) => editarListas({ setor_id: v[0] ?? null }, { setor_ids: v.slice(1) })}
                  data-campo-rapido="setor_id"
                />
                <LinhaEditavel
                  rotulo={t('crm.campos.site')}
                  tipo="url"
                  exibir={e.site}
                  apos={
                    e.site && (
                      <a href={/^https?:\/\//i.test(e.site) ? e.site : `https://${e.site}`} target="_blank" rel="noreferrer noopener" aria-label={t('crm.aj.abrirSite', { site: e.site })} title={t('crm.aj.abrirSite', { site: e.site })} className="flex size-11 shrink-0 items-center justify-center rounded-lg text-azul hover:bg-sup-2 md:size-9">
                        <ExternalLink className="size-4" aria-hidden />
                      </a>
                    )
                  }
                  valor={e.site ?? ''}
                  pode={pode}
                  aoSalvar={(v) => editarCampo({ site: v || null })}
                  data-campo-rapido="site"
                />
                {/* crm5 (E2): o e-mail da empresa, pelo formatador do modo discreto. */}
                <LinhaEditavel rotulo={t('crm.campos.email')} exibir={e.email ? email(e.email) : ''} valor={e.email ?? ''} pode={pode} aoSalvar={(v) => editarCampo({ email: v.trim() || null })} data-campo-rapido="email" />
                {/* Lista de 02/11 (item 3): o que o pop-up passou a gravar e a ficha não mostrava (razão social, telefones e e-mails adicionais). */}
                {(e.fiscal?.razao_social || e.telefone || (e.meios ?? []).length > 0) && (
                  <dl data-dados-contato-empresa="">
                    {(e.meios ?? []).filter((m) => m.meio === 'email').map((m) => (
                      <Fragment key={m.id}>{linha(t('crm.campos.email'), email(m.valor))}</Fragment>
                    ))}
                    {e.fiscal?.razao_social && linha(t('crm.campos.razaoSocial'), nomePessoa(e.fiscal.razao_social))}
                    {e.telefone && linha(e.telefone_tipo ? t(`crm.aj.tiposTelefone.${e.telefone_tipo === 'pessoal' ? 'outro' : e.telefone_tipo}`) : t('crm.campos.telefone'), telefoneFormatado(e.telefone))}
                    {(e.meios ?? []).filter((m) => m.meio === 'telefone').map((m) => (
                      <Fragment key={m.id}>{linha(t(`crm.aj.tiposTelefone.${m.tipo === 'pessoal' ? 'outro' : m.tipo}`), telefoneFormatado(m.valor))}</Fragment>
                    ))}
                  </dl>
                )}
                <EnderecoEditavel rotulo={t('crm.campos.endereco')} atual={e.enderecos[0]} exibir={e.enderecos[0] ? endereco(e.enderecos[0] as never) : ''} pode={pode} aoSalvar={editarEndereco} data-campo-rapido="endereco" />
                {/* crm6 item 11: o regime tributário em vigor (os períodos se editam no cadastro completo). */}
                <dl data-regime-empresa={regimeVigente ?? ''}>{linha(t('campos.regime'), regimeVigente ? t(`valores.regime.${regimeVigente}`) : '')}</dl>
                {defsEmpresa.length + extrasOrfaos.length > 0 && (
                  <div data-extras-empresa="">
                    {defsEmpresa.map((d) => (
                      <ExtraEditavel
                        key={d.nome}
                        def={d}
                        extras={e.extras}
                        nomes={[e.nome]}
                        pode={pode}
                        aoSalvar={(x) => editarCampo({ extras: x })}
                        apos={
                          d.nome === 'extras.linkedin' &&
                          linkedin && (
                            <a href={linkedin} target="_blank" rel="noreferrer noopener" data-abrir-linkedin="" aria-label={t('crm.lbEmpresa.abrirLinkedin', { perfil: valorExtra(defLinkedin, e.extras?.linkedin, e.nome) })} title={t('crm.lbEmpresa.abrirLinkedin', { perfil: valorExtra(defLinkedin, e.extras?.linkedin, e.nome) })} className="flex size-11 shrink-0 items-center justify-center rounded-lg text-azul hover:bg-sup-2 md:size-9">
                              <ExternalLink className="size-4" aria-hidden />
                            </a>
                          )
                        }
                      />
                    ))}
                    {extrasOrfaos.length > 0 && <dl>{extrasOrfaos.map((x) => <div key={x.chave}>{linha(x.rotulo, x.valor)}</div>)}</dl>}
                  </div>
                )}
              </div>
            </Secao>
            <Secao titulo={t('crm.aj.observacoes')} aberta={secoes.sobre} aoAlternar={() => setSecoes((x) => ({ ...x, sobre: !x.sobre }))}>
              <LinhaEditavel rotulo={t('crm.aj.resumoEmpresa')} tipo="area" exibir={textoLivre(e.observacoes, e.nome)} valor={observacoesCampo} pode={pode} aoSalvar={(v) => editarCampo({ observacoes: v || null })} data-campo-rapido="observacoes" />
            </Secao>
            {veCadastroCompleto && (
              <Link to={`/cadastros/cadastros/parceiro/${e.id}`} className="mt-3 inline-block text-sm text-azul underline" data-cadastro-completo="">
                {t('crm.empresas.cadastroCompleto')}
              </Link>
            )}
          </Cartao>
          {/* crm8 (1): as verticais vêm das negociações e dos contratos; aqui só leitura. */}
          <CartaoVerticaisRelacionamento parceiroId={e.id} />
          <Cartao
            titulo={t('crm.empresas.contatosAssociados')}
            acao={
              // Lista de 02/11 (item 5b): o pop-up central de busca precisa de `cadastros.editar` (a rota do vínculo); só com
              // `cadastros.criar`, o botão segue abrindo o cadastro, como antes.
              (podeEditar || s.pode('cadastros.criar')) &&
              !e.arquivado && (
                <Botao tamanho="sm" variante="fantasma" icone={<Plus className="size-4" />} onClick={() => (podeEditar ? setAdicionarContato(true) : setNovoContato(true))} data-abrir-adicionar-contato="">
                  {t('acoes.adicionarContato')}
                </Botao>
              )
            }
          >
            {e.contatos.length ? (
              <ul className="flex flex-col" data-contatos-empresa="">
                {e.contatos.map((c) => {
                  const aberto = contatoAberto === c.id;
                  return (
                    <li key={c.id} className="border-b border-borda last:border-0">
                      <button type="button" aria-expanded={aberto} onClick={() => setContatoAberto(aberto ? null : c.id)} className="flex min-h-11 w-full items-center gap-2 rounded-lg px-2 text-left text-sm hover:bg-sup-2">
                        <Iniciais nome={c.nome} />
                        <span className="min-w-0 flex-1 truncate font-semibold text-tinta-1" title={nomePessoa(c.nome)}>{nomePessoa(c.nome)}</span>
                        {c.adicional && <span className="shrink-0 text-xs text-tinta-3" data-contato-adicional="">({t('crm.aj.adicional')})</span>}
                        {aberto ? <ChevronUp className="size-4 text-tinta-3" aria-hidden /> : <ChevronDown className="size-4 text-tinta-3" aria-hidden />}
                      </button>
                      {aberto && (
                        <div className="flex flex-col gap-1 px-2 pb-3 text-sm text-tinta-2" data-contato-expandido="">
                          {c.cargo && <span>{textoLivre(c.cargo, c.nome)}</span>}
                          <span className="flex items-center gap-1.5">
                            <Phone className="size-3.5 text-tinta-3" aria-hidden />
                            <span className="sr-only">{t('crm.campos.telefone')}: </span>
                            {telefoneFormatado(c.telefone) || <span className="text-tinta-3">{t('crm.campos.naoPreenchido')}</span>}
                          </span>
                          <span>
                            {t('crm.aj.linkedin')}: {c.linkedin ? valorExtra(extrasDefs.find((d) => d.nome === 'extras.linkedin'), c.linkedin, c.nome) : <span className="text-tinta-3">{t('crm.campos.naoPreenchido')}</span>}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Mail className="size-3.5 text-tinta-3" aria-hidden />
                            <span className="sr-only">{t('crm.campos.email')}: </span>
                            {email(c.email) || <span className="text-tinta-3">{t('crm.campos.naoPreenchido')}</span>}
                          </span>
                          <Link to={`${CAMINHO_CONTATOS}/${c.id}`} className="text-azul underline">
                            {t('crm.aj.verMais')}
                          </Link>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-tinta-3">{t('crm.empresas.semContatos')}</p>
            )}
          </Cartao>
          <CartaoFiliais key={`filiais-${e.id}`} e={e} />
          <CartaoMatriz key={`matriz-${e.id}`} e={e} />
          <Cartao titulo={t('crm.empresas.atribuicoes')}>
            <div className="px-2 py-2 text-sm">
              <p className="mb-2 text-tinta-3">{t('crm.campos.seguidores')}</p>
              <div className="flex flex-wrap gap-2" data-seguidores="">
                {e.seguidores.map((x) => (
                  <span key={x.id} className="inline-flex items-center gap-1">
                    <Selo>{nomePessoa(x.nome)}</Selo>
                    {pode && (
                      <button type="button" aria-label={t('crm.seguir.remover', { nome: nomePessoa(x.nome) })} onClick={() => void seguir(x.id, true)} className="flex size-8 items-center justify-center rounded text-tinta-3 hover:bg-sup-2">
                        <X className="size-3.5" aria-hidden />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {pode && (
                <div className="mt-2 flex gap-2">
                  <Selecao
                    aria-label={t('crm.seguir.adicionar')}
                    value={seguidor}
                    onChange={(ev) => setSeguidor(ev.target.value)}
                    vazio={t('crm.seguir.adicionar')}
                    opcoes={(membros.data?.itens ?? []).filter((m) => !e.seguidores.some((x) => x.id === m.id)).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))}
                  />
                  <Botao tamanho="sm" disabled={!seguidor} onClick={() => void seguir(seguidor)}>
                    {t('acoes.adicionar')}
                  </Botao>
                </div>
              )}
            </div>
            <LinhaMultiplaEditavel
              rotulo={t('crm.campos.responsavel')}
              exibir={textoDosResponsaveis(e, t('crm.lbEmpresa.todos'))}
              valores={responsaveisDoRegistro(e)}
              opcoes={opcoesMembros}
              rotuloPrincipal={t('crm.aj.principal')}
              pode={pode}
              ajuda={e.responsavel_todos ? t('crm.lbEmpresa.todosAjuda') : undefined}
              aoSalvar={(v) => {
                const r = corpoDosResponsaveis(v);
                return editarListas(r.principal, r.adicionais);
              }}
              data-campo-rapido="responsavel_id"
            />
          </Cartao>
        </div>
        <div className="min-w-0 lg:col-span-2">
          <Abas
            grupo={idAbas}
            rotulo={nomePessoa(e.nome)}
            ativa={aba}
            aoMudar={setAba}
            itens={[
              ...(veNegociacoes ? [{ chave: 'negociacoes' as const, rotulo: t('crm.empresas.negociacoes') }] : []),
              { chave: 'historico' as const, rotulo: t('crm.empresas.historico') },
            ]}
          />
          {aba === 'negociacoes' && veNegociacoes ? (
            <PainelAba idBase={idAbas} chave="negociacoes" className="mt-4 flex flex-col gap-4">
              {/* Rodada rcn-5 (ROB-B2): primeira leitura com falha não vira zeros, como se fossem dado real. */}
              {!negs.data ? (
                negs.isError ? (
                  <Erro erro={negs.error} aoTentar={() => negs.refetch()} />
                ) : (
                  <Carregando />
                )
              ) : (
              <>
              {negs.isError && <Erro erro={negs.error} aoTentar={() => negs.refetch()} />}
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-3" data-indicadores-empresa="">
                <Indicador rotulo={t('crm.empresas.emAndamento')} valor={somaPorMoeda(proprias.filter((n) => n.estado === 'aberta'))} fundo="ciano" ajuda={t('crm.aj.ajudaAndamento')} />
                <Indicador rotulo={t('crm.empresas.vendido')} valor={somaPorMoeda(ganhas)} fundo="verde" ajuda={t('crm.aj.ajudaVendido')} />
                <Indicador rotulo={t('crm.empresas.perdido')} valor={somaPorMoeda(proprias.filter((n) => n.estado === 'perdida'))} fundo="perigo" ajuda={t('crm.aj.ajudaPerdido')} />
                <Indicador rotulo={t('crm.empresas.totalNegociacoes')} valor={numero(proprias.length)} />
                <Indicador rotulo={t('crm.empresas.ticketMedio')} valor={ganhas.length ? somaPorMoeda(ganhas.map((g) => ({ ...g, valor_centavos: g.valor_centavos / ganhas.length }))) : dinheiro(0)} />
                <Indicador rotulo={t('crm.empresas.tempoMedio')} valor={numero(diasAteVenda)} />
              </div>
              <Cartao semPadding>
                <NegociacoesDaEmpresa key={e.id} lista={lista} nomes={[e.nome, ...e.contatos.map((c) => c.nome)]} empresaId={e.id} />
              </Cartao>
              </>
              )}
            </PainelAba>
          ) : (
            <PainelAba idBase={idAbas} chave="historico" className="mt-4">
              <HistoricoEmpresa e={e} negociacoes={veNegociacoes ? (negs.data ?? []) : []} />
            </PainelAba>
          )}
        </div>
      </div>
      <FormEmpresa
        aberto={editar}
        aoFechar={() => {
          setEditar(false);
        }}
        atual={e}
        aoSalvar={() => void recarregar()}
      />
      <AdicionarContatoEmpresa
        aberto={adicionarContato}
        aoFechar={() => setAdicionarContato(false)}
        empresaId={e.id}
        aoCadastrarNovo={
          s.pode('cadastros.criar')
            ? () => {
                setAdicionarContato(false);
                setNovoContato(true);
              }
            : undefined
        }
      />
      <FormContato aberto={novoContato} aoFechar={() => setNovoContato(false)} empresa={{ id: e.id, nome: e.nome }} aoSalvar={() => void recarregar()} criarOutra />
      {negociar && <CriarNegociacao aberto parceiros={[{ id: e.id, nome: e.nome }]} aoFechar={() => setNegociar(false)} aoCriar={() => void recarregar()} />}
      <DialogoCriacao aberta={criacao.aberta} aoFechar={criacao.fechar} contexto={{ empresa: { id: e.id, nome: e.nome } }} aoCriar={() => void qc.invalidateQueries({ queryKey: ['crm', 'empresa', id] })} />
      {feedback.dialogo}
      <ArquivarParceiro
        id={e.id}
        aberto={inativar}
        aoFechar={() => setInativar(false)}
        textos={{
          pergunta: t('crm.inativar.titulo'),
          texto: t('crm.inativar.texto', { nome: nomePessoa(e.nome) }),
          feito: t('crm.inativar.feito'),
          emUsoTitulo: t('crm.inativar.emUsoTitulo'),
          emUsoTexto: t('crm.inativar.emUsoTexto'),
        }}
        // A lista do servidor diz módulo e quantidade; os títulos vêm das negociações abertas que a pessoa já vê na ficha.
        detalhes={{ pipeline: proprias.filter((n) => n.estado === 'aberta').map((n) => textoLivre(n.titulo, e.nome, ...e.contatos.map((c) => c.nome))) }}
        aoArquivado={() => {
          setInativar(false);
          void qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
          void qc.invalidateQueries({ queryKey: ['crm'] });
          navegar(CAMINHO_EMPRESAS);
        }}
      />
      <Confirmar
        aberto={reativar}
        aoFechar={() => setReativar(false)}
        titulo={t('crm.inativar.reativarEmpresa')}
        texto={t('crm.inativar.reativarUmaTexto', { nome: nomePessoa(e.nome) })}
        aoConfirmar={async () => {
          try {
            await post(`/cadastros/parceiros/${e.id}/desarquivar`, {});
            toast.sucesso(t('crm.inativar.reativada'));
            await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
            await qc.invalidateQueries({ queryKey: ['crm'] });
          } catch (err) {
            toast.erro(mensagemDeErro(err));
          }
          setReativar(false);
        }}
      />
    </div>
  );
}
